import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Truck,
  CreditCard,
  Activity,
  ExternalLink,
  Copy,
  CheckCircle,
  XCircle,
  Settings,
  BookOpen,
  FlaskConical,
  Info,
  BrainCircuit,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Reusable Components ──────────────────────────────────────────────────────

function EnvRow({ label, envKey, description, secret = false }) {
  const copy = () => {
    navigator.clipboard.writeText(envKey);
    toast.success(`${envKey} copied`);
  };

  return (
    <div className="grid grid-cols-[1fr_auto] gap-4 py-3 items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono text-accent">{envKey}</code>
          {secret && (
            <Badge className="text-[10px] bg-orange-500/10 text-orange-400 border-orange-500/20">secret</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        onClick={copy}
        className="p-1.5 text-muted-foreground hover:text-foreground transition-colors mt-0.5 shrink-0"
        title="Copy env key"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function StatusDot({ ok }) {
  return ok ? (
    <CheckCircle className="h-4 w-4 text-green-500 shrink-0" strokeWidth={1.5} />
  ) : (
    <XCircle className="h-4 w-4 text-red-500/70 shrink-0" strokeWidth={1.5} />
  );
}

function SectionHeader({ icon: Icon, color, title, badge, badgeVariant = 'yellow' }) {
  const colorMap = {
    accent: 'bg-accent/10 text-accent',
    purple: 'bg-purple-500/10 text-purple-400',
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  const badgeColorMap = {
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    green: 'bg-green-500/10 text-green-400 border-green-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colorMap[color] || colorMap.accent}`}>
        <Icon className="h-4.5 w-4.5" strokeWidth={1.5} />
      </div>
      <div>
        <h3 className="font-outfit font-semibold text-base">{title}</h3>
      </div>
      {badge && (
        <Badge className={`text-[10px] ml-auto ${badgeColorMap[badgeVariant]}`}>{badge}</Badge>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/connector-health`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setHealth)
      .catch(() => {});
  }, [token]);

  const cdOk = health?.central_dispatch?.configured && health?.central_dispatch?.enabled;
  const stripeOk = health?.stripe?.configured && health?.stripe?.enabled;
  const llmOk = health?.llm?.configured;

  return (
      <div className="space-y-8 max-w-4xl" data-testid="settings-page">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Settings className="h-6 w-6 text-accent" strokeWidth={1.5} />
            Settings &amp; API Configuration
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Environment variables, test credentials, and integration reference for all connected services.
          </p>
        </div>

        {/* ── Integration Status Banner ──────────────────────────────────── */}
        <Card className="bg-card border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <Activity className="h-4 w-4 text-accent" strokeWidth={1.5} />
              Integration Status
            </CardTitle>
            <CardDescription>Live status from /api/admin/connector-health</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Central Dispatch', ok: cdOk, loading: !health },
                { label: 'Stripe Payments', ok: stripeOk, loading: !health },
                { label: 'LLM / AI', ok: llmOk, loading: !health },
              ].map(({ label, ok, loading }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-muted/30"
                  data-testid={`status-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {loading ? (
                    <div className="h-4 w-4 rounded-full border-2 border-accent border-t-transparent animate-spin shrink-0" />
                  ) : (
                    <StatusDot ok={ok} />
                  )}
                  <span className="text-sm">{label}</span>
                  <Badge
                    className={`ml-auto text-[10px] ${
                      loading
                        ? 'bg-muted text-muted-foreground'
                        : ok
                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                        : 'bg-red-500/10 text-red-400 border-red-500/20'
                    }`}
                  >
                    {loading ? 'checking…' : ok ? 'Active' : 'Not configured'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Central Dispatch ──────────────────────────────────────────── */}
        <Card className="bg-card border-white/5" data-testid="cd-settings-section">
          <CardHeader>
            <SectionHeader
              icon={Truck}
              color="accent"
              title="Central Dispatch Integration"
              badge="Test Credentials"
              badgeVariant="yellow"
            />
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Test environment info */}
            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-yellow-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium text-yellow-300">Test / Sandbox Environment</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  { label: 'API Base URL', value: 'https://listings-api.centraldispatch.com' },
                  { label: 'Auth Token URL', value: 'https://id.centraldispatch.com/connect/token' },
                  { label: 'Auth Flow', value: 'Client Credentials (OAuth 2.0)' },
                  { label: 'Grant Type', value: 'client_credentials' },
                  { label: 'Accept Header', value: 'application/vnd.coxauto.v1+json' },
                  { label: 'Content-Type (POST)', value: 'application/json' },
                  { label: 'Test Client ID', value: '123456-789-10111213-14151617' },
                  { label: 'Test Client Secret', value: '123456-789-10111213-14151617' },
                  { label: 'Test Marketplace ID', value: '100099' },
                  { label: 'API Access', value: 'Listings, Fulfillment, Documents, Membership, Events' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{label}</span>
                    <code
                      className="font-mono text-foreground/80 text-[11px] cursor-pointer hover:text-accent"
                      onClick={() => {
                        navigator.clipboard.writeText(value);
                        toast.success(`${label} copied`);
                      }}
                    >
                      {value}
                    </code>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <a
                  href="https://api-docs.centraldispatch.com/testing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  <BookOpen className="h-3 w-3" />
                  Testing Guide &amp; Test Accounts
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a
                  href="https://api-docs.centraldispatch.com/authentication"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  <BookOpen className="h-3 w-3" />
                  Authentication Docs
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a
                  href="https://api-docs.centraldispatch.com/request-form"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Request API Access Form
                </a>
              </div>
            </div>

            {/* Test accounts */}
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium text-blue-300">Test Accounts</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-xs">
                <div className="space-y-1.5">
                  <p className="font-medium text-foreground/80 uppercase tracking-wider text-[10px]">Test Shipper</p>
                  {[
                    { label: 'Company ID', value: 'abcdefg' },
                    { label: 'Phone', value: '(Used for test account verification)' },
                    { label: 'Email', value: '(Used for test account verification)' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex flex-col gap-0.5">
                      <span className="text-muted-foreground">{label}</span>
                      <code className="font-mono text-foreground/80 text-[11px]">{value}</code>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <p className="font-medium text-foreground/80 uppercase tracking-wider text-[10px]">Test Carrier</p>
                  {[
                    { label: 'Customer ID', value: 'abcdefgh-ijk1m-nopq-12345' },
                    { label: 'Phone', value: '(Used for test account verification)' },
                    { label: 'Email', value: '(Used for test account verification)' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex flex-col gap-0.5">
                      <span className="text-muted-foreground">{label}</span>
                      <code className="font-mono text-foreground/80 text-[11px]">{value}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Getting test credentials instructions */}
            <div className="rounded-xl border border-white/5 bg-muted/20 p-4 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-blue-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium">How to get sandbox credentials</span>
              </div>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside leading-relaxed">
                <li>Complete the <a href="https://api-docs.centraldispatch.com/request-form" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">API Request Form</a> — allow up to 3 business days.</li>
                <li>You will receive a <code className="bg-muted px-1 rounded">client_id</code> and <code className="bg-muted px-1 rounded">client_secret</code> — or use the test credentials above.</li>
                <li>Set <code className="bg-muted px-1 rounded">CD_CLIENT_ID</code> and <code className="bg-muted px-1 rounded">CD_CLIENT_SECRET</code> in your backend <code className="bg-muted px-1 rounded">.env</code>. The backend fetches tokens automatically.</li>
                <li>Set <code className="bg-muted px-1 rounded">CD_BASE_URL=https://listings-api.centraldispatch.com</code>, <code className="bg-muted px-1 rounded">CD_ACCOUNT_ID=100099</code>, and <code className="bg-muted px-1 rounded">CENTRAL_DISPATCH_ENABLED=true</code>.</li>
                <li>For test UI accounts (carrier / shipper), visit the <a href="https://api-docs.centraldispatch.com/testing" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Testing &amp; User Guides</a> page.</li>
              </ol>
            </div>

            {/* Env vars */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                Environment Variables
              </p>
              <div className="divide-y divide-white/5 rounded-xl border border-white/5 bg-muted/20 px-3">
                <EnvRow
                  envKey="CENTRAL_DISPATCH_ENABLED"
                  description="Set to 'true' to enable the CD integration. Default: false"
                />
                <EnvRow
                  envKey="CD_CLIENT_ID"
                  secret
                  description="OAuth2 client_id from Central Dispatch (test: 123456-789-10111213-14151617)"
                />
                <EnvRow
                  envKey="CD_CLIENT_SECRET"
                  secret
                  description="OAuth2 client_secret from Central Dispatch (test: 123456-789-10111213-14151617)"
                />
                <EnvRow
                  envKey="CD_BASE_URL"
                  description="Listings API base URL. Default: https://listings-api.centraldispatch.com"
                />
                <EnvRow
                  envKey="CD_ACCOUNT_ID"
                  description="Your shipper account ID (test: 100099)"
                />
                <EnvRow
                  envKey="CD_MARKETPLACE_ID"
                  description="Target marketplace ID. Default: 40177"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Stripe ────────────────────────────────────────────────────── */}
        <Card className="bg-card border-white/5" data-testid="stripe-settings-section">
          <CardHeader>
            <SectionHeader
              icon={CreditCard}
              color="purple"
              title="Stripe Payments"
              badge="Test Mode"
              badgeVariant="purple"
            />
          </CardHeader>
          <CardContent className="space-y-6">

            <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-purple-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium text-purple-300">Test Cards</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  { label: 'Success card', value: '4242 4242 4242 4242' },
                  { label: 'Auth required', value: '4000 0025 0000 3155' },
                  { label: 'Declined card', value: '4000 0000 0000 9995' },
                  { label: 'Insufficient funds', value: '4000 0000 0000 9995' },
                  { label: 'Expiry (any future)', value: 'MM / YY' },
                  { label: 'CVC', value: 'Any 3 digits' },
                  { label: 'ZIP', value: 'Any 5 digits' },
                  { label: 'Webhook CLI', value: 'stripe listen --forward-to localhost:8001/api/webhook/stripe' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{label}</span>
                    <code
                      className="font-mono text-foreground/80 text-[11px] cursor-pointer hover:text-purple-400"
                      onClick={() => {
                        navigator.clipboard.writeText(value);
                        toast.success(`${label} copied`);
                      }}
                    >
                      {value}
                    </code>
                  </div>
                ))}
              </div>
              <a
                href="https://dashboard.stripe.com/test/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-purple-400 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Stripe Dashboard → Developers → API Keys
              </a>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                Environment Variables
              </p>
              <div className="divide-y divide-white/5 rounded-xl border border-white/5 bg-muted/20 px-3">
                <EnvRow
                  envKey="STRIPE_MODE"
                  description="'test' or 'live' — switches which key pair is active. Default: test"
                />
                <EnvRow
                  envKey="STRIPE_SECRET_KEY_TEST"
                  secret
                  description="Stripe secret key for test mode (sk_test_…)"
                />
                <EnvRow
                  envKey="STRIPE_SECRET_KEY_LIVE"
                  secret
                  description="Stripe secret key for live mode (sk_live_…) — required when STRIPE_MODE=live"
                />
                <EnvRow
                  envKey="STRIPE_WEBHOOK_SECRET_TEST"
                  secret
                  description="Webhook signing secret for test mode — from Stripe Dashboard → Developers → Webhooks"
                />
                <EnvRow
                  envKey="STRIPE_WEBHOOK_SECRET_LIVE"
                  secret
                  description="Webhook signing secret for live mode"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── LLM / AI ──────────────────────────────────────────────────── */}
        <Card className="bg-card border-white/5" data-testid="llm-settings-section">
          <CardHeader>
            <SectionHeader
              icon={BrainCircuit}
              color="blue"
              title="LLM / AI"
              badge="OpenAI"
              badgeVariant="blue"
            />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 text-xs space-y-2">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-400 shrink-0" strokeWidth={1.5} />
                <span className="text-sm font-medium text-blue-300">AI Conversation Engine</span>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Used to generate the initial quote confirmation message and power the AI chat on each load.
                Set <code className="bg-muted px-1 rounded">LLM_PROVIDER=openai</code> and supply an API key.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                Environment Variables
              </p>
              <div className="divide-y divide-white/5 rounded-xl border border-white/5 bg-muted/20 px-3">
                <EnvRow
                  envKey="OPENAI_API_KEY"
                  secret
                  description="OpenAI API key (sk-…) — required when LLM_PROVIDER=openai"
                />
                <EnvRow
                  envKey="LLM_PROVIDER"
                  description="LLM backend. Default: openai"
                />
                <EnvRow
                  envKey="LLM_MODEL"
                  description="Model to use. Default: gpt-4o"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── All env vars reference ─────────────────────────────────────── */}
        <Card className="bg-card border-white/5" data-testid="env-reference-section">
          <CardHeader>
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-accent" strokeWidth={1.5} />
              All Environment Variables Reference
            </CardTitle>
            <CardDescription>
              Complete list of backend <code>.env</code> variables read by <code>config.py</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-white/5 rounded-xl border border-white/5 bg-muted/20 px-3">
              {[
                { envKey: 'DATABASE_URL', secret: true, description: 'PostgreSQL connection string: postgresql+asyncpg://user:pass@host:5432/db' },
                { envKey: 'JWT_SECRET', secret: true, description: 'Token signing secret — must be 64+ random characters in production' },
                { envKey: 'CORS_ORIGINS', description: 'Comma-separated allowed origins. Must include the frontend URL.' },
                { envKey: 'STRIPE_MODE', description: "'test' or 'live' — switches the active Stripe key pair. Default: test" },
                { envKey: 'STRIPE_SECRET_KEY_TEST', secret: true, description: 'Stripe secret key for test mode (sk_test_…)' },
                { envKey: 'STRIPE_SECRET_KEY_LIVE', secret: true, description: 'Stripe secret key for live mode (sk_live_…)' },
                { envKey: 'STRIPE_WEBHOOK_SECRET_TEST', secret: true, description: 'Webhook signing secret for test mode' },
                { envKey: 'STRIPE_WEBHOOK_SECRET_LIVE', secret: true, description: 'Webhook signing secret for live mode' },
                { envKey: 'ADMIN_ALLOWLIST', description: 'Comma-separated emails allowed to log into the admin panel' },
                { envKey: 'SUPERADMIN_EMAILS', description: 'Comma-separated emails that can force margin overrides' },
                { envKey: 'ADMIN_NOTIFICATION_EMAIL', description: 'Recipient for KPI alert emails' },
                { envKey: 'OPENAI_API_KEY', secret: true, description: 'OpenAI API key for LLM-powered features' },
                { envKey: 'LLM_PROVIDER', description: 'LLM backend. Default: openai' },
                { envKey: 'LLM_MODEL', description: 'Model to use. Default: gpt-4o' },
                { envKey: 'CENTRAL_DISPATCH_ENABLED', description: "Enable CD listing + dispatch — 'true' or 'false'. Default: false" },
                { envKey: 'CD_CLIENT_ID', secret: true, description: 'CD OAuth2 client_id' },
                { envKey: 'CD_CLIENT_SECRET', secret: true, description: 'CD OAuth2 client_secret' },
                { envKey: 'CD_ACCOUNT_ID', description: 'CD shipper account ID' },
                { envKey: 'CD_MARKETPLACE_ID', description: 'CD marketplace to post loads to. Default: 40177' },
                { envKey: 'CD_BASE_URL', description: 'CD API base. Default: https://listings-api.centraldispatch.com' },
                { envKey: 'VOICE_CALLS_ENABLED', description: "Enable voice call feature. Default: false" },
                { envKey: 'GOOGLE_DISTANCE_API_ENABLED', description: "Use Google Maps for distance. Default: false (uses haversine)" },
                { envKey: 'MARGIN_OVERRIDE_ENABLED', description: "Allow superadmins to bypass margin floor. Default: false" },
                { envKey: 'ALERT_EMAIL_ENABLED', description: "Send KPI alert emails. Default: false" },
                { envKey: 'RATE_LIMIT_ENABLED', description: "Enable per-IP rate limiting. Default: true" },
                { envKey: 'FRAUD_SCORE_THRESHOLD', description: 'Score at or above this value blocks a quote request. Default: 3' },
                { envKey: 'RATE_LIMIT_QUOTES_PER_HOUR', description: 'Max quote submissions per IP per hour. Default: 10' },
                { envKey: 'RATE_LIMIT_DEPOSITS_PER_HOUR', description: 'Max deposit attempts per IP per hour. Default: 3' },
                { envKey: 'ALERT_REFUND_RATE_THRESHOLD', description: 'KPI alert: refund rate % above this fires an alert. Default: 30' },
                { envKey: 'ALERT_DISPATCH_SUCCESS_THRESHOLD', description: 'KPI alert: dispatch success % below this fires an alert. Default: 60' },
                { envKey: 'ALERT_DISPUTE_RATE_THRESHOLD', description: 'KPI alert: dispute rate % threshold. Default: 5' },
                { envKey: 'ALERT_FRAUD_BLOCK_THRESHOLD', description: 'KPI alert: fraud block rate % threshold. Default: 10' },
                { envKey: 'MAX_ESCALATIONS', description: 'Max carrier pay-cap ladder bumps per load. Default: 3' },
                { envKey: 'ESCALATION_INTERVAL_MINUTES', description: 'Minutes between automatic pay-cap escalations. Default: 20' },
              ].map((row) => (
                <EnvRow key={row.envKey} {...row} />
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
  );
}
